/**
 * Which panels are offered for each shot data `type`, and how each maps onto the
 * `shot_image` request body. The agent's DTO carries the data `type`; the panel
 * tabs let the user switch the rendered panel without re-asking the agent.
 *
 * NOTE: the request-body encoding below (the `panel` index and the interleave
 * flag) mirrors the legacy shot-image viewer:
 *   - Predicted  → panel 0
 *   - Field      → panel 1
 *   - Difference → panel 2
 *   - Interleaved → interleave_attrs.is_interleave = 1
 * The `compare` panel index is provisional — confirm it against the backend and
 * adjust here; nothing else needs to change.
 */

/** A single selectable panel and the request-body overrides it applies. */
export interface ShotPanel {
  /** Stable key used to track the selected tab. */
  key: string;
  /** Tab label shown to the user. */
  label: string;
  /**
   * Partial `shot_image` request body applied when this panel is selected.
   * `interleave_attrs` is shallow-merged onto the agent's payload so the other
   * interleave fields (extent, scaling, …) are preserved.
   */
  body: {
    panel?: number;
    interleave_attrs?: { is_interleave: 0 | 1 };
  };
}

const PREDICTED: ShotPanel = {
  key: "predicted",
  label: "Predicted",
  body: { panel: 0, interleave_attrs: { is_interleave: 0 } },
};

const FIELD: ShotPanel = {
  key: "field",
  label: "Field",
  body: { panel: 1, interleave_attrs: { is_interleave: 0 } },
};

const INTERLEAVED: ShotPanel = {
  key: "interleaved",
  label: "Interleaved",
  body: { interleave_attrs: { is_interleave: 1 } },
};

const COMPARE: ShotPanel = {
  key: "compare",
  label: "Compare",
  // Provisional panel index — verify against the backend.
  body: { panel: 2, interleave_attrs: { is_interleave: 0 } },
};

/** Panels available per data `type`. Extend this map as new types are added. */
export const SHOT_PANELS_BY_TYPE: Record<string, ShotPanel[]> = {
  "lowpass-B4": [PREDICTED, FIELD, INTERLEAVED],
  compare: [PREDICTED, FIELD, COMPARE, INTERLEAVED],
};

/** Used when the data `type` has no explicit entry above. */
export const DEFAULT_SHOT_PANELS: ShotPanel[] = [PREDICTED, FIELD, INTERLEAVED];

/** Resolve the panel list for a data `type`, falling back to the default set. */
export function panelsForType(type: string | undefined): ShotPanel[] {
  return (type && SHOT_PANELS_BY_TYPE[type]) || DEFAULT_SHOT_PANELS;
}
